import { CircleAlert, EyeOff, Heart, Info, LoaderCircle, MessageCircleOff, PanelRight, Trash2 } from 'lucide-react';
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { CapturedMedia } from './captured-media';
import { RuleScores } from './rule-scores';
import { actionTypeLabel } from '../action-log-constants';
import {
	confirmShortfallSentence,
	dynamicReplyNotice,
	isUnsentDraft,
	type DynamicReplyNotice
} from '../action-log-verdict';
import {
	commentTextOrMediaAlone,
	confidencePillClasses,
	formatConfidence,
	isScore,
	isJevDecision,
	orNotRecorded,
	relativeAge
} from '../action-log-format';
import type { MetaChannel } from '@/modules/integrations/meta-contract';
import type { SocialActionLogRow, SocialMediaSnapshotRef } from '../types';

/**
 * One decision, opened from a row — Figma 4775:12189.
 *
 * A panel rather than an expanded row: reading a decision means reading the comment and the reply
 * as a conversation, and an inline block wide enough to do that pushed the rest of the log off
 * screen. The list stays where it was.
 *
 * Everything here comes from the audit row. Nothing is fetched, because the row IS the record — a
 * comment we deleted may no longer exist on Meta to fetch.
 */

interface ActionLogDetailPanelProps {
	row: SocialActionLogRow | null;
	/** The connected Meta accounts, the only place a channel id maps to a name and a picture. */
	channels: MetaChannel[];
	onOpenChange: (open: boolean) => void;
}

/** "Instagram" / "Facebook" — which network the account is on. */
const platformLabel = (platform: SocialActionLogRow['platform']): string =>
	platform === 'instagram' ? 'Instagram' : 'Facebook';

/**
 * The avatar's letter — the first LETTER, not the first character.
 *
 * Handles like `_iam.srb_` start with punctuation, and an underscore alone in a circle reads as a
 * rendering fault rather than as an initial.
 */
const initialOf = (name: string | null): string => {
	const letter = name?.trim().match(/[a-z0-9]/i)?.[0];
	return letter ? letter.toUpperCase() : '?';
};

/** What was done TO the comment, drawn on the comment itself. */
interface CommentState {
	Icon: typeof EyeOff;
	label: string;
}

/**
 * Hidden and deleted are the two actions that change what the public can see, and neither leaves
 * any other trace on this panel — the comment renders exactly as it would had nothing happened.
 * Reply does not qualify: the reply IS its own evidence, sitting right underneath.
 *
 * Only a `fired` row draws one. `fired` is the single decision meaning the action was carried out,
 * so a blocked or queued hide shows nothing: the comment is still up, and a badge claiming
 * otherwise would be a false statement in the one place that has to be true.
 */
const commentStateOf = (row: SocialActionLogRow): CommentState | null => {
	if (row.decision !== 'fired') return null;
	if (row.actionType === 'auto_hide') return { Icon: EyeOff, label: 'Hidden' };
	if (row.actionType === 'auto_delete') return { Icon: Trash2, label: 'Deleted' };
	return null;
};

/**
 * A reply Kim wrote and the agent held back. It rides the same slot as Hidden and Deleted,
 * because it is the same kind of fact: what the public can and cannot see.
 */
const UNSENT_DRAFT_STATE: CommentState = { Icon: MessageCircleOff, label: 'Draft, not sent' };

const Message = ({
	author,
	age,
	body,
	isBrand,
	pictureUrl,
	liked,
	state,
	media,
	draft = false
}: {
	author: string;
	age: string | null;
	/** Null when the attachment is the whole comment — see `commentTextOrMediaAlone`. */
	body: string | null;
	isBrand: boolean;
	/**
	 * The brand's own avatar, from the connect payload. Only the reply can have one: a commenter's
	 * picture is not on the comment node and needs a grant no brand has made, so their avatar stays
	 * an initial.
	 */
	pictureUrl?: string | null;
	/** Only the customer's comment can carry this — the agent never likes its own reply. */
	liked?: boolean | null;
	/** Hidden or deleted, and only on the comment that was acted on. */
	state?: CommentState | null;
	/** The attachments captured with this comment, if any. */
	media?: SocialMediaSnapshotRef[] | null;
	/**
	 * Text that was never posted. Drawn in a dashed outline and muted, with no heart, so it cannot
	 * be read as a reply that is sitting under the comment in public.
	 */
	draft?: boolean;
}) => (
	// The brand's reply is indented under the comment it answers, as Instagram indents a reply.
	// Level, the two read as two separate comments on the post rather than as an exchange.
	<div className={cn('flex gap-3', isBrand && 'pl-6')}>
		<Avatar className='mt-0.5 size-8 shrink-0'>
			{pictureUrl && <AvatarImage src={pictureUrl} alt='' />}
			<AvatarFallback className={cn('bg-gray-100 text-[11px] text-gray-900', isBrand && 'bg-gray-200')}>
				{initialOf(author)}
			</AvatarFallback>
		</Avatar>
		{/* Who and when on one line, what they said on the next — Figma 4775:12561. Running the
		    handle into the text the way Instagram sets a comment made the two indistinguishable at
		    a glance, and the reader of an audit log is scanning for the text, not the handle. */}
		<div
			className={cn(
				'flex min-w-0 flex-1 flex-col gap-0.5',
				draft && 'rounded-lg border border-dashed border-gray-500 px-2 py-1.5'
			)}
		>
			{/* The state belongs to the comment, so it rides this line rather than the decision
			    table further down. */}
			<div className='flex min-w-0 items-baseline gap-1.5 text-[11px] leading-4 text-gray-800'>
				<span
					className={cn(
						'truncate text-[13px] font-semibold leading-[18px]',
						draft ? 'text-gray-900' : 'text-ink-strong'
					)}
				>
					{author}
				</span>
				{age && <span className='shrink-0'>{age}</span>}
				{age && state && <span aria-hidden='true'>·</span>}
				{state && (
					<span className='inline-flex shrink-0 items-center gap-1 font-medium text-gray-900'>
						<state.Icon className='size-3' aria-hidden='true' />
						{state.label}
					</span>
				)}
			</div>
			{/* Omitted entirely, not left empty, when the attachment is the whole comment — an empty
			    paragraph still takes a line and opens a gap above the image. */}
			{body !== null && (
				<p
					className={cn(
						'whitespace-pre-wrap text-[13px] leading-[18px] break-words',
						draft ? 'text-gray-900' : 'text-ink'
					)}
				>
					{body}
				</p>
			)}
			{/* What the agent actually looked at, under the words it read. Only the customer's side
			    can carry one — the brand's reply is text the rule already holds. */}
			<CapturedMedia refs={media} />
		</div>

		{/*
		 * Every message carries a heart, at the right edge, so the liked ones read as a filled slot
		 * in a column rather than as a mark that appears from nowhere.
		 *
		 * Filled only when the like actually landed. `false` means the rule asked and Meta refused
		 * and `null` means nothing asked at all — neither is a liked comment, so both draw the
		 * outline.
		 */}
		{/* Not on a draft: nothing was posted, so there is nothing a heart could be on. */}
		{!draft && (
			<Heart
				className={cn(
					'mt-1 size-3.5 shrink-0',
					liked === true ? 'fill-red-500 text-red-500' : 'text-gray-600'
				)}
				aria-label={liked === true ? 'Liked by the brand' : 'Not liked'}
			/>
		)}
	</div>
);

/** How each tone of notice is drawn. Pill tokens, so it matches the decision pill in the table. */
const NOTICE_TONES: Record<DynamicReplyNotice['tone'], { classes: string; Icon: typeof EyeOff }> = {
	info: { classes: 'bg-blue-100 text-blue-700', Icon: LoaderCircle },
	danger: { classes: 'bg-red-100 text-red-700', Icon: CircleAlert }
};

/**
 * What became of a dynamic reply that is not simply posted, said outright.
 *
 * Out in the open rather than behind the score's info button: "why was nothing sent?" is the
 * first thing a reader of this row asks, and the answer is the point of the row.
 */
const ReplyNotice = ({ notice }: { notice: DynamicReplyNotice }) => {
	const { classes, Icon } = NOTICE_TONES[notice.tone];

	return (
		<div role='note' className={cn('flex flex-col gap-1 rounded-lg px-3 py-2.5', classes)}>
			<p className='flex items-center gap-1.5 text-[13px] font-medium leading-[18px]'>
				<Icon className='size-4 shrink-0' aria-hidden='true' />
				{notice.title}
			</p>
			<p className='text-xs leading-[17px]'>{notice.body}</p>
			{notice.why && (
				<p className='text-xs leading-[17px]'>
					<span className='font-medium'>Why:</span> {notice.why}
				</p>
			)}
		</div>
	);
};

/** A score as a pill, coloured by whether it cleared the floor it was held to. */
const ScorePill = ({ value, threshold }: { value: number; threshold: number | null | undefined }) => (
	<span
		className={cn(
			'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
			confidencePillClasses(value, threshold)
		)}
	>
		{formatConfidence(value)}
	</span>
);

/**
 * The floor Kim's reply was held to, and what Kim said about its own confidence.
 *
 * The floor is the brand's own setting as it stood when the reply was judged, read off the row.
 */
const ReplyScoreExplanation = ({ row }: { row: SocialActionLogRow }) => {
	const reasoning = row.replyConfidenceReason?.trim();
	if (!isScore(row.replyThreshold) && !reasoning) return null;

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<button
					type='button'
					aria-label='How this reply was judged'
					className='rounded-full p-0.5 text-gray-800 hover:text-ink'
				>
					<Info className='size-3.5' />
				</button>
			</TooltipTrigger>
			<TooltipContent variant='dark' className='max-w-[240px] text-left'>
				<div className='flex flex-col gap-1.5'>
					{isScore(row.replyThreshold) && (
						<p>
							A reply needs {formatConfidence(row.replyThreshold)}. Kim&rsquo;s reply reached{' '}
							{formatConfidence(row.replyConfidence)}.
						</p>
					)}
					{reasoning && <p>Kim said: {reasoning}</p>}
				</div>
			</TooltipContent>
		</Tooltip>
	);
};

/**
 * Why this score is what it is, behind an info button.
 *
 * Behind a button rather than as more rows because most of the time the number is enough. What it
 * has to answer when the number is NOT enough is the question the bare arithmetic provokes: a rule
 * the system chose, then scored at zero, reads as the engine contradicting itself. It is not —
 * Match nominates every rule a comment could satisfy and is explicitly told ranking is not its job,
 * and Confirm then asks the stricter question about the winner alone. So the tooltip carries the
 * floor, what the confirmation answered, and the quote Match nominated on.
 *
 * Drawn only when there is something to say. An empty tooltip on every row would train the reader
 * to ignore the one row that has an explanation.
 *
 * A Jev row has no confirmation check and no quoted evidence, so it keeps only the floor and what
 * the verifier said.
 */
const ScoreExplanation = ({ row }: { row: SocialActionLogRow }) => {
	const decidedByJev = isJevDecision(row);
	const shortfall = decidedByJev ? null : confirmShortfallSentence(row);
	const evidence = decidedByJev ? undefined : row.evidenceSpan?.trim();

	// `isScore`, not `=== null`. `riskThreshold` is `number | null | undefined`, so an === null
	// check lets `undefined` through as though a threshold were present — and the sentence below
	// then renders "needs —." mid-sentence, because formatConfidence answers the em dash for both.
	if (!isScore(row.riskThreshold) && !row.verifierReason && !shortfall && !evidence) return null;

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<button
					type='button'
					aria-label='How this score was judged'
					className='rounded-full p-0.5 text-gray-800 hover:text-ink'
				>
					<Info className='size-3.5' />
				</button>
			</TooltipTrigger>
			<TooltipContent variant='dark' className='max-w-[240px] text-left'>
				<div className='flex flex-col gap-1.5'>
					{isScore(row.riskThreshold) && (
						<p>
							{actionTypeLabel(row.actionType)} needs {formatConfidence(row.riskThreshold)}. This match reached{' '}
							{formatConfidence(row.matchedConfidence)}.
						</p>
					)}

					{shortfall && <p>{shortfall}</p>}

					{/* The other half of the disagreement: what Match quoted to nominate the rule. Confirm
					    cannot supply a reason — it is a one-word answer, which is exactly what makes its
					    score calibrated — so this is the only thing on the row explaining the nomination. */}
					{evidence && (
						<p className='text-gray-300'>
							Match relied on: <span className='italic'>&ldquo;{evidence}&rdquo;</span>
						</p>
					)}

					{row.verifierReason && <p>The verifier said: {row.verifierReason}</p>}
				</div>
			</TooltipContent>
		</Tooltip>
	);
};

const REPLY_TYPE_LABELS = { dynamic: 'Dynamic', fixed: 'Fixed' } as const;

const DetailRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
	<div className='flex items-start justify-between gap-4 py-2'>
		<span className='text-[13px] text-gray-900'>{label}</span>
		<div className='min-w-0 text-right text-[13px] text-ink'>{children}</div>
	</div>
);

export const ActionLogDetailPanel = ({ row, channels, onOpenChange }: ActionLogDetailPanelProps) => {
	const channel = channels.find((entry) => entry.channelId === row?.channelId);
	// The snapshotted handle is the fallback: it records what the channel was CALLED when it acted,
	// which survives a rename and survives the account being disconnected entirely.
	const pageName = channel?.name || channel?.handle || row?.channelHandle || row?.channelId || 'Channel';
	const unsentDraft = row !== null && isUnsentDraft(row);
	const replyNotice = row ? dynamicReplyNotice(row) : null;

	return (
	<Sheet open={row !== null} onOpenChange={onOpenChange}>
		{/*
		 * Opaque, over a light scrim. The shared sheet is 80% white on a black/40 overlay — right for
		 * a transient dialog, wrong here: every avatar, pill and line of comment text was
		 * compositing onto grey, which is what made the panel look muddy. This is a reading surface,
		 * and the table behind it stays legible on purpose.
		 */}
		<SheetContent
			side='right'
			showClose={false}
			overlayClassName='bg-black/10'
			className='flex w-[340px] flex-col gap-0 bg-white p-0 backdrop-blur-none sm:max-w-[340px]'
		>
			{row && (
				<>
					{/* The rule sits under the header, closing it off — it separates the panel's title
					    bar from its content. On the page-details block below it read as a divider
					    between the account and the thread, which are the two things that belong
					    together. */}
					<div className='flex items-start justify-between gap-3 border-b border-gray-300 px-4 pt-4 pb-3'>
						<div className='flex min-w-0 flex-col'>
							<SheetTitle className='text-[15px] font-semibold text-ink-strong'>
								#{orNotRecorded(row.ticketDisplayId)}
							</SheetTitle>
							<SheetDescription className='text-[11px] leading-4 text-gray-800'>
								{actionTypeLabel(row.actionType)}
							</SheetDescription>
						</div>
						{/* The design's collapse control. Closing is what it does — the panel has no
						    half-open state to toggle to. */}
						<SheetClose className='rounded-md p-1 text-gray-800 hover:bg-gray-100' aria-label='Close'>
							<PanelRight className='size-4' />
						</SheetClose>
					</div>

					<div className='flex flex-1 flex-col gap-4 overflow-y-auto px-4 pt-4 pb-6'>
						{/*
						 * The account this happened on, not a restatement of the platform. "Instagram
						 * Comment" said nothing the Decision and Post Link below did not; which of the
						 * brand's connected accounts replied under its own name is the thing a reader
						 * cannot work out from anywhere else on this panel.
						 *
						 * The picture comes from the connect payload. It is the only avatar Meta will
						 * give us — a commenter's is not on the comment node and needs a grant they
						 * have never made.
						 */}
						<div className='flex items-center gap-2.5'>
							<Avatar className='size-8 shrink-0'>
								{channel?.pictureUrl && <AvatarImage src={channel.pictureUrl} alt='' />}
								<AvatarFallback className='text-[11px]'>{initialOf(pageName)}</AvatarFallback>
							</Avatar>
							<div className='flex min-w-0 flex-col'>
								<span className='truncate text-[13px] font-semibold text-ink-strong'>{pageName}</span>
								<span className='truncate text-[11px] leading-4 text-gray-800'>
									{platformLabel(row.platform)}
									{channel?.handle && ` · @${channel.handle}`}
								</span>
							</div>
						</div>

						{/*
						 * The thread sits in its own tinted card. Page details and comments were two
						 * avatar-and-name blocks stacked with the same spacing, so the account the
						 * comment is ON read as another participant IN it. The card says one is the
						 * setting and the others are the conversation.
						 */}
						<div className='flex flex-col gap-3 rounded-xl bg-gray-50 p-3'>
							<Message
								author={row.commenterUsername ?? 'Commenter'}
								age={relativeAge(row.commentCreatedAt)}
								body={commentTextOrMediaAlone(row.commentSnapshot, row.mediaSnapshotRefs?.length ?? 0)}
								isBrand={false}
								liked={row.commentLiked}
								state={commentStateOf(row)}
								media={row.mediaSnapshotRefs}
							/>
							{/* Only replies post text. A hide or a delete has nothing to show here, and an
							    empty bubble would suggest a reply that failed to send. */}
							{row.responseText && (
								<Message
									author={row.channelHandle ?? 'Kim'}
									// A draft was never posted, so it has no age to state.
									age={unsentDraft ? null : relativeAge(row.createdAt)}
									body={row.responseText}
									isBrand
									pictureUrl={channel?.pictureUrl}
									draft={unsentDraft}
									state={unsentDraft ? UNSENT_DRAFT_STATE : null}
								/>
							)}
						</div>

						{replyNotice && <ReplyNotice notice={replyNotice} />}

						<div className='flex flex-col'>
							<h3 className='pb-1 text-[13px] font-semibold text-ink-strong'>Details</h3>

							<DetailRow label='Rule Triggered'>{orNotRecorded(row.ruleName)}</DetailRow>

							{row.replySource && (
								<DetailRow label='Reply Type'>{REPLY_TYPE_LABELS[row.replySource]}</DetailRow>
							)}

							{/*
							 * The score alone says nothing: 78% reads as a near miss until you know the
							 * floor it was measured against, and a blocked action reads as a fault until
							 * you know the verifier objected and why. Both are on the row and neither was
							 * being shown, so the panel is where they go — behind an info button rather
							 * than as two more rows, because most of the time the number is enough.
							 */}
							<DetailRow label='Confidence Score'>
								<div className='flex items-center justify-end gap-1'>
									{row.matchedConfidence === null ? (
										orNotRecorded(null)
									) : (
										<ScorePill value={row.matchedConfidence} threshold={row.riskThreshold} />
									)}
									<ScoreExplanation row={row} />
								</div>
							</DetailRow>

							{/* A dynamic reply is judged twice: the rule match above, then the reply Kim wrote.
							    Only rows with a reply score carry this second number. */}
							{isScore(row.replyConfidence) && (
								<DetailRow label='Reply Confidence'>
									<div className='flex items-center justify-end gap-1'>
										<ScorePill value={row.replyConfidence} threshold={row.replyThreshold} />
										<ReplyScoreExplanation row={row} />
									</div>
								</DetailRow>
							)}

							<DetailRow label='Decision'>{row.decision}</DetailRow>

							<RuleScores row={row} />

							<DetailRow label='Post Link'>
								{row.mediaPermalink ? (
									<a
										href={row.mediaPermalink}
										target='_blank'
										rel='noreferrer noopener'
										className='text-blue-600 hover:underline'
									>
										View the post
									</a>
								) : (
									orNotRecorded(null)
								)}
							</DetailRow>
						</div>
					</div>
				</>
			)}
		</SheetContent>
	</Sheet>
);
};
